/**
 * Custom fields on the item page, on the tab each field names (Settings →
 * Custom Fields → Shows on). Drawn like "What it is": the field's name, its
 * value in words, and Edit opening the field's own editor in place — the same
 * editor "At a glance" and the quick panel use. A child field shows under its
 * parent once the parent has a value.
 */

import { useNavigate } from 'react-router-dom';
import { CustomFieldInput } from '@/components/prioritization/item-view/CustomFieldsSection';
import { btnSecondary } from '@/components/mono/primitives';
import { fieldsOnTab, formatCustomFieldValue, isEnabledItemField } from '@/lib/customFieldPlacement';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { CustomFieldPlacementTab } from '@/types/prioritization';
import { ActionLinkView, OwnedSectionHeading, WhatItIsRowList, type WhatItIsRow } from './ItemDetailThirdPassView';

export function ItemCustomFields({
  tab,
  metadata,
  readOnly,
  onSave,
}: {
  tab: CustomFieldPlacementTab;
  metadata: Record<string, unknown> | undefined;
  readOnly: boolean;
  onSave: (updates: { metadata: Record<string, unknown> }) => void;
}) {
  const attributes = useAttributesStore((s) => s.attributes);
  const mayManage = useUserStore((s) => s.can('attributes.define') || s.can('admin.settings'));
  const navigate = useNavigate();
  const fields = fieldsOnTab(attributes, tab);
  if (fields.length === 0) return null;

  const meta = metadata ?? {};
  const rows: WhatItIsRow[] = fields.flatMap((field) => {
    const parentSet = meta[field.key] != null && meta[field.key] !== '';
    const children = parentSet ? attributes.filter((a) => isEnabledItemField(a) && a.parentKey === field.key) : [];
    return [field, ...children].map((f) => ({
      id: f.key,
      label: f.label,
      text: formatCustomFieldValue(f, meta[f.key]),
      editor: readOnly
        ? undefined
        : (close: () => void) => (
            <div>
              <CustomFieldInput field={f} metadata={meta} onSave={onSave} />
              <div className="mt-2">
                <button type="button" {...btnSecondary()} onClick={close}>
                  Done
                </button>
              </div>
            </div>
          ),
    }));
  });

  return (
    <div data-testid={`item-custom-fields-${tab}`}>
      <OwnedSectionHeading title="Custom fields" owned="your workspace's fields · edit here">
        {mayManage && <ActionLinkView action={{ label: 'Manage fields', onClick: () => navigate('/prioritization/settings/custom-fields') }} />}
      </OwnedSectionHeading>
      <WhatItIsRowList rows={rows} scope={`custom-${tab}`} />
    </div>
  );
}
